import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, SolidButton, StatusIndicator } from '../buttons-visuals';
import { ContentContainer } from '../containers';
import Modal from '../modal';
import { useProfile } from '../../hooks/store';
import {
    ProvaAttemptResultSummary,
    ProvaPracticeStatus,
    ProvaPracticeTitleOption,
    ProvaService,
} from '../../services/provaService';

import './index.scss';

interface ProvaPracticeProps {
    /** When set, shows that user's attempts (profile). */
    userId?: string;
    /** Offer Start Solo Play (dashboard / league / own profile). */
    showStart?: boolean;
    /** Tighter layout for the profile right column. */
    compact?: boolean;
}

interface SoloGameTile {
    id: string;
    name: string;
    imageUrl?: string;
    titles: ProvaPracticeTitleOption[];
}

const formatScore = (attempt: Core.Models.ProvaAttempt): string => {
    if (typeof attempt.competitiveScore === 'number') return attempt.competitiveScore.toFixed(1).replace(/\.0$/, '');
    if (typeof attempt.overallScore === 'number') return attempt.overallScore.toFixed(1).replace(/\.0$/, '');
    return '—';
};

const statusLabel = (status: string): string => {
    switch ((status || '').toLowerCase()) {
        case 'scored':
            return 'Completed';
        case 'launched':
            return 'Ready to play';
        case 'inprogress':
            return 'In progress';
        case 'disqualified':
            return 'Disqualified';
        case 'incomplete':
            return 'Incomplete';
        case 'noshow':
            return 'No show';
        case 'cancelled':
            return 'Cancelled';
        default:
            return status || 'Unknown';
    }
};

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch ((status || '').toLowerCase()) {
        case 'scored':
            return 'success';
        case 'launched':
        case 'inprogress':
            return 'warning';
        case 'disqualified':
        case 'noshow':
        case 'cancelled':
            return 'error';
        default:
            return 'default';
    }
};

const formatWhen = (iso: string): string => {
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const isOpenAttempt = (status: string): boolean =>
    ['launched', 'inprogress'].includes((status || '').toLowerCase());

const canOpenRecap = (status: string): boolean =>
    ['scored', 'disqualified', 'incomplete'].includes((status || '').toLowerCase());

const formatRubricScore = (score?: number, max?: number): string => {
    if (typeof score !== 'number') return '—';
    const scoreText = score.toFixed(1).replace(/\.0$/, '');
    if (typeof max === 'number' && max > 0) return `${scoreText} / ${max}`;
    return scoreText;
};

const ProvaPractice = (props: ProvaPracticeProps): JSX.Element => {
    const { userId, showStart = !userId, compact = false } = props;
    const profile = useProfile();
    const isSchoolMember = !!profile?.organizationId;

    const [attempts, setAttempts] = useState<Core.Models.ProvaAttempt[]>([]);
    const [status, setStatus] = useState<ProvaPracticeStatus | undefined>();
    const [catalogGames, setCatalogGames] = useState<Core.Models.ProvaCatalogGame[]>([]);
    const [loading, setLoading] = useState(true);
    const [startingKey, setStartingKey] = useState<string | undefined>();
    const [selectedGameId, setSelectedGameId] = useState<string | undefined>();
    const [recapAttemptId, setRecapAttemptId] = useState<string | undefined>();
    const [recap, setRecap] = useState<ProvaAttemptResultSummary | undefined>();
    const [recapLoading, setRecapLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [attemptData, statusData, gamesData] = await Promise.all([
                userId ? ProvaService.listAttemptsForUser(userId) : ProvaService.listMyAttempts(),
                showStart ? ProvaService.getStatus() : Promise.resolve(undefined),
                showStart ? ProvaService.getCatalogGames().catch(() => [] as Core.Models.ProvaCatalogGame[]) : Promise.resolve([]),
            ]);
            setAttempts(attemptData);
            setCatalogGames(gamesData);
            if (statusData) {
                setStatus({
                    ...statusData,
                    titles: statusData.titles ?? [],
                });
            }
        } catch (e) {
            if (showStart) toast.error(Core.API.getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [showStart, userId]);

    useEffect(() => {
        void load();
    }, [load]);

    const titles = status?.titles ?? [];
    const titleNameByKey = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of titles) map.set(t.titleKey.toLowerCase(), t.name);
        return map;
    }, [titles]);

    const games = useMemo((): SoloGameTile[] => {
        const byId = new Map<string, SoloGameTile>();

        for (const game of catalogGames) {
            byId.set(game.id, {
                id: game.id,
                name: game.name,
                imageUrl: game.cardUrl || game.iconUrl,
                titles: [],
            });
        }

        for (const title of titles) {
            const id = title.gameId || title.gameName || title.titleKey;
            const existing = byId.get(id);
            if (existing) {
                existing.titles.push(title);
            } else {
                byId.set(id, {
                    id,
                    name: title.gameName || 'Solo Play',
                    titles: [title],
                });
            }
        }

        return [...byId.values()]
            .filter((game) => game.titles.length > 0 || !showStart)
            .map((game) => ({
                ...game,
                titles: [...game.titles].sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })
                ),
            }))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [catalogGames, showStart, titles]);

    const selectedGame = useMemo(
        () => games.find((game) => game.id === selectedGameId),
        [games, selectedGameId]
    );

    const canStart = useMemo(
        () => showStart && isSchoolMember && !!status?.canStart && titles.length > 0,
        [isSchoolMember, showStart, status?.canStart, titles.length]
    );

    /** Contract v4: redirect the browser to sessionUrl immediately; do not store or display it. */
    const redirectToSession = (attempt: Core.Models.ProvaAttempt) => {
        const url = attempt.sessionUrl || attempt.joinUrl;
        if (!url) {
            toast.error('Solo Play opened but no session link was returned.');
            return false;
        }
        window.location.assign(url);
        return true;
    };

    const start = useCallback(
        async (titleKey: string) => {
            if (startingKey || !canStart || !titleKey) return;
            setStartingKey(titleKey);
            try {
                const attempt = await ProvaService.startAttempt(titleKey, window.location.href);
                if (!redirectToSession(attempt)) {
                    setStartingKey(undefined);
                }
                // On success the page navigates away; no further UI work.
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
                setStartingKey(undefined);
            }
        },
        [canStart, startingKey]
    );

    const resume = useCallback(
        async (attemptId: string) => {
            if (startingKey || !attemptId) return;
            setStartingKey(attemptId);
            try {
                const attempt = await ProvaService.openSession(attemptId, window.location.href);
                if (!redirectToSession(attempt)) {
                    setStartingKey(undefined);
                }
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
                setStartingKey(undefined);
            }
        },
        [startingKey]
    );

    const openRecap = useCallback(async (attemptId: string) => {
        setRecapAttemptId(attemptId);
        setRecap(undefined);
        setRecapLoading(true);
        try {
            const summary = await ProvaService.getAttemptResult(attemptId);
            setRecap(summary);
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
            setRecapAttemptId(undefined);
        } finally {
            setRecapLoading(false);
        }
    }, []);

    const closeRecap = useCallback(() => {
        setRecapAttemptId(undefined);
        setRecap(undefined);
        setRecapLoading(false);
    }, []);

    const heading = showStart ? 'Solo Play' : 'Solo Play scores';
    const lede = showStart
        ? selectedGame
            ? `Choose a challenge in ${selectedGame.name}. Play anytime on your own — no class session needed. Your score shows up here and on your profile.`
            : status?.message ||
              'Play challenges on your own whenever you want — no class session needed. Pick a game, choose a challenge, and your score shows up here and on your profile.'
        : 'Scores from completed Solo Play sessions.';

    const emptyMessage = (() => {
        if (showStart && status && !status.available) return status.message;
        if (showStart && !isSchoolMember) return 'Join your school on LeagueSpot to start Solo Play.';
        if (showStart && titles.length === 0) {
            return status?.message || 'Solo Play challenges will appear here once they are available.';
        }
        if (showStart) return 'No Solo Play sessions yet. Pick a game when you are ready.';
        return 'No Solo Play scores yet.';
    })();

    const resolveAttemptName = (titleKey?: string) =>
        (titleKey && titleNameByKey.get(titleKey.toLowerCase())) || titleKey || 'Solo Play session';

    const recapTitleName = recap ? resolveAttemptName(recap.titleKey) : 'Solo Play recap';
    const recapScore =
        typeof recap?.competitiveScore === 'number'
            ? recap.competitiveScore.toFixed(1).replace(/\.0$/, '')
            : typeof recap?.overallScore === 'number'
              ? recap.overallScore.toFixed(1).replace(/\.0$/, '')
              : '—';

    return (
        <div className={classNames('prova-practice-wrap', { 'prova-practice-wrap--compact': compact })} id="solo-play">
            <ContentContainer
                className="prova-practice shadow-medium p0"
                shade={Core.Models.Shades.Dark20}
                radius={Core.Models.RadiusSizes.Large}
            >
                <div className="prova-practice__header">
                    <div className="prova-practice__heading-block">
                        {selectedGame && canStart && (
                            <HollowButton as="button" size="small" onClick={() => setSelectedGameId(undefined)}>
                                All games
                            </HollowButton>
                        )}
                        <h2 className="heading-2 mb0" tabIndex={-1}>
                            {selectedGame && canStart ? selectedGame.name : heading}
                        </h2>
                    </div>
                </div>

                <div className="prova-practice__body">
                    {showStart && <p className="prova-practice__lede">{lede}</p>}

                    {canStart && !selectedGame && games.length > 0 && (
                        <div className="prova-practice__game-grid" role="list">
                            {games.map((game) => (
                                <button
                                    key={game.id}
                                    type="button"
                                    className="prova-practice__game-tile"
                                    role="listitem"
                                    onClick={() => setSelectedGameId(game.id)}
                                >
                                    {game.imageUrl ? (
                                        <img src={game.imageUrl} alt="" />
                                    ) : (
                                        <span className="prova-practice__game-tile-fallback" aria-hidden />
                                    )}
                                    <span className="prova-practice__game-tile-shade" />
                                    <span className="prova-practice__game-tile-meta">
                                        <span className="prova-practice__game-tile-name">{game.name}</span>
                                        <span className="prova-practice__game-tile-count">
                                            {game.titles.length} challenge{game.titles.length === 1 ? '' : 's'}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {canStart && selectedGame && (
                        <ul className="prova-practice__challenges">
                            {selectedGame.titles.map((title) => (
                                <li key={title.titleKey} className="prova-practice__challenge">
                                    <span className="prova-practice__challenge-name">{title.name}</span>
                                    <SolidButton
                                        as="button"
                                        size="small"
                                        attention
                                        onClick={() => void start(title.titleKey)}
                                        pending={startingKey === title.titleKey}
                                    >
                                        Play
                                    </SolidButton>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="prova-practice__scores">
                        <h3 className="prova-practice__scores-title heading-3">Recent scores</h3>
                        {loading ? (
                            <p className="prova-practice__empty">Loading Solo Play…</p>
                        ) : attempts.length === 0 ? (
                            <div className="prova-practice__empty-state">
                                <p className="prova-practice__empty">{emptyMessage}</p>
                            </div>
                        ) : (
                            <ul className="prova-practice__list">
                                {attempts.map((attempt) => {
                                    const scored = canOpenRecap(attempt.status);
                                    return (
                                        <li key={attempt.id} className="prova-practice__row">
                                            <button
                                                type="button"
                                                className={classNames('prova-practice__row-main', {
                                                    'prova-practice__row-main--clickable': scored,
                                                })}
                                                onClick={() => scored && void openRecap(attempt.id)}
                                                disabled={!scored}
                                            >
                                                <span className="prova-practice__title">
                                                    {resolveAttemptName(attempt.titleKey)}
                                                </span>
                                                <span className="prova-practice__date">
                                                    {formatWhen(attempt.completedAtUtc || attempt.createdUtc)}
                                                    {scored ? ' · View recap' : ''}
                                                </span>
                                            </button>
                                            <StatusIndicator color={statusColor(attempt.status)} outlineOnly>
                                                {statusLabel(attempt.status)}
                                            </StatusIndicator>
                                            <strong className="prova-practice__score">{formatScore(attempt)}</strong>
                                            {showStart && isOpenAttempt(attempt.status) && (
                                                <div className="prova-practice__actions">
                                                    <HollowButton
                                                        as="button"
                                                        size="small"
                                                        onClick={() => void resume(attempt.id)}
                                                        pending={startingKey === attempt.id}
                                                    >
                                                        Resume
                                                    </HollowButton>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </ContentContainer>

            {!!recapAttemptId && (
                <Modal className="prova-recap-modal" onClose={closeRecap} title="Solo Play recap">
                    {recapLoading || !recap ? (
                        <p className="prova-recap__empty">Loading your recap…</p>
                    ) : (
                        <div className="prova-recap">
                            <div className="prova-recap__hero">
                                <div className="prova-recap__hero-copy">
                                    <p className="prova-recap__eyebrow mb0">Challenge complete</p>
                                    <h3 className="heading-3 mb">{recapTitleName}</h3>
                                    <p className="prova-recap__meta mb0">
                                        {formatWhen(recap.completedAtUtc || recap.createdUtc)}
                                        {recap.integrity ? ` · ${recap.integrity}` : ''}
                                    </p>
                                </div>
                                <div className="prova-recap__score-block">
                                    <span className="prova-recap__score-label">Score</span>
                                    <strong className="prova-recap__score-value">{recapScore}</strong>
                                    <StatusIndicator color={statusColor(recap.status)} outlineOnly>
                                        {statusLabel(recap.status)}
                                    </StatusIndicator>
                                </div>
                            </div>

                            {(recap.feedback || recap.overallExplanation) && (
                                <section className="prova-recap__section">
                                    <h4 className="prova-recap__section-title">Coach note</h4>
                                    <p className="prova-recap__feedback mb0">
                                        {recap.feedback || recap.overallExplanation}
                                    </p>
                                </section>
                            )}

                            {recap.rubrics?.length > 0 && (
                                <section className="prova-recap__section">
                                    <h4 className="prova-recap__section-title">What stood out</h4>
                                    <ul className="prova-recap__rubrics">
                                        {recap.rubrics.map((rubric, index) => (
                                            <li
                                                key={`${rubric.title}-${index}`}
                                                className="prova-recap__rubric"
                                            >
                                                <div className="prova-recap__rubric-head">
                                                    <span className="prova-recap__rubric-title">{rubric.title}</span>
                                                    <strong className="prova-recap__rubric-score">
                                                        {formatRubricScore(rubric.score, rubric.max)}
                                                    </strong>
                                                </div>
                                                {rubric.strengths && (
                                                    <p className="prova-recap__rubric-line">
                                                        <span className="prova-recap__tag">Strength</span>
                                                        {rubric.strengths}
                                                    </p>
                                                )}
                                                {rubric.weaknesses && (
                                                    <p className="prova-recap__rubric-line">
                                                        <span className="prova-recap__tag prova-recap__tag--warn">
                                                            Grow
                                                        </span>
                                                        {rubric.weaknesses}
                                                    </p>
                                                )}
                                                {!rubric.strengths && !rubric.weaknesses && rubric.reasoning && (
                                                    <p className="prova-recap__rubric-line">{rubric.reasoning}</p>
                                                )}
                                                {rubric.quote && (
                                                    <blockquote className="prova-recap__quote">
                                                        “{rubric.quote}”
                                                    </blockquote>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {!recap.feedback &&
                                !recap.overallExplanation &&
                                !(recap.rubrics?.length > 0) && (
                                    <p className="prova-recap__empty">
                                        Score is in. Detailed coaching for this challenge isn’t available yet.
                                    </p>
                                )}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};

export default ProvaPractice;
